// 📋 TODO: Implement recording, transfer
import {
  BodyText,
  Button,
  ButtonGroup,
  Popover,
  Stack,
  Tag,
  Tooltip,
} from '@servicetitan/design-system';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './CallControls.module.css';
import CallControlKeypad from '@app/layouts/MainLayout/components/CallBar/components/CallControlKeypad';
import { inboxLocators } from '@locators/inbox';
import { CallControlsDivider } from './CallControlsDivider';

interface CallControlsProps {
  show: boolean;
  established: boolean;
  hasSession: boolean;
  held: boolean;
  inTransfer: boolean;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  progress: boolean;
  identity: string; // remote identity, number or name that is displayed in the call bar
  terminating: boolean;
  formattedTime: string;
  isRecording: boolean;
  isRecordingDisabled: boolean;
  isRecordingLoading: boolean;
  recordingTooltip: string;
  onHangup: VoidFunction;
  onAnswer: VoidFunction;
  onHold: VoidFunction;
  onMute: VoidFunction;
  onUnhold: VoidFunction;
  onUnmute: VoidFunction;
  onStartTimer: VoidFunction;
  toggleRecording: VoidFunction;
}

const CallControls: React.FC<CallControlsProps> = ({
  show,
  established,
  held,
  isAudioMuted,
  identity,
  terminating,
  formattedTime,
  onHangup,
  onAnswer,
  onHold,
  onMute,
  onUnhold,
  onUnmute,
  onStartTimer,
  isRecording,
  isRecordingDisabled,
  isRecordingLoading,
  recordingTooltip,
  toggleRecording,
}) => {
  useEffect(() => {
    onStartTimer();
    // Because it's in a useCallback in the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canAnswer = terminating && !established;
  const canHangup = !terminating || (terminating && established);
  const [isKeypadOpened, setIsKeypadOpened] = useState(false);
  const recordingAriaLabel = (() => {
    if (isRecordingDisabled) {
      return 'Recording button disabled';
    }
    if (isRecording) {
      return 'Stop Recording';
    }
    return 'Start Recording';
  })();

  return (
    <div
      className={classNames(
        { 'd-n': !show },
        styles.stickyContainer,
        'z-overlay',
        'position-sticky',
      )}
      id={inboxLocators.callBar.callControls}
    >
      <Stack
        className={`${styles.callControlsContainer} p-r-4 p-l-3 bg-neutral-400`}
        alignItems='center'
        justifyContent='space-between'
      >
        <Stack alignItems='center' spacing={2}>
          <BodyText
            className='c-white fw-bold'
            id={inboxLocators.callBar.displayNameChecker}
          >
            {identity}
          </BodyText>
          <BodyText className='c-neutral-10' size='small'>
            {formattedTime}
          </BodyText>
          {held && (
            <Tag
              className='bg-neutral-200-i c-neutral-0-i fs-1'
              title='On Hold'
            >
              On Hold
            </Tag>
          )}
        </Stack>
        <ButtonGroup>
          {isAudioMuted ? (
            <Tooltip
              direction='top'
              id={inboxLocators.callBar.unmuteBtnTooltip}
              text='Unmute'
            >
              <Button
                aria-label='Unmute'
                className={styles.callControlButtonActiveCritical}
                iconName='mic_off'
                id={inboxLocators.callBar.unmuteBtn}
                onClick={onUnmute}
              />
            </Tooltip>
          ) : (
            <Tooltip
              direction='top'
              text='Mute'
              id={inboxLocators.callBar.muteBtnTooltip}
            >
              <Button
                aria-label='Mute'
                iconName='mic'
                className={styles.callControlButtonDefault}
                id={inboxLocators.callBar.muteBtn}
                onClick={onMute}
              />
            </Tooltip>
          )}
          <Tooltip direction='top' text='Transfer not yet implemented'>
            <Button
              aria-label='Transfer'
              className={styles.callControlButtonDefault}
              disabled
              iconName='phone_forwarded'
              onClick={/* onTransfer */ () => {}}
            />
          </Tooltip>
          <Tooltip
            direction='top'
            id={inboxLocators.callBar.pauseRecordingAudioCallTooltip}
            text={recordingTooltip}
          >
            <Button
              aria-label={recordingAriaLabel}
              className={
                isRecording
                  ? styles.callControlButtonActiveCritical
                  : styles.callControlButtonDefault
              }
              id={inboxLocators.callBar.pauseRecordingAudioCallBtn}
              disabled={isRecordingLoading || isRecordingDisabled}
              iconName='radio_button_checked'
              onClick={toggleRecording}
            />
          </Tooltip>
          {held ? (
            <Tooltip
              direction='top'
              id={inboxLocators.callBar.resumeCallBtnTooltip}
              text='Resume Call'
            >
              <Button
                aria-label='Resume Call'
                className={styles.callControlButtonActive}
                iconName='pause'
                id={inboxLocators.callBar.resumeCallBtn}
                onClick={onUnhold}
              />
            </Tooltip>
          ) : (
            <Tooltip
              direction='top'
              id={inboxLocators.callBar.placeCallOnHoldBtnTooltip}
              text='Hold'
            >
              <Button
                aria-label='Hold'
                className={styles.callControlButtonDefault}
                iconName='pause'
                id={inboxLocators.callBar.placeCallOnHoldBtn}
                onClick={onHold}
              />
            </Tooltip>
          )}
          <Popover
            className={classNames('bg-neutral-500', 'border-style-none')}
            wrapperClassName={styles.callControlPopperWrapper}
            open={isKeypadOpened}
            trigger={
              <Tooltip
                direction='top'
                text={isKeypadOpened ? 'Close Keypad' : 'Keypad'}
              >
                <Button
                  className={classNames({
                    [styles.callControlButtonDefault]: !isKeypadOpened,
                    [styles.callControlButtonActive]: isKeypadOpened,
                  })}
                  aria-label='Keypad'
                  iconName='dialpad'
                  onClick={() => setIsKeypadOpened(!isKeypadOpened)}
                />
              </Tooltip>
            }
          >
            <CallControlKeypad
              open={isKeypadOpened}
              onClose={() => setIsKeypadOpened(false)}
            />
          </Popover>
          {canHangup && <CallControlsDivider />}
          {canHangup && (
            <Tooltip direction='top' text='End Call'>
              <Button
                id={inboxLocators.callBar.endCallButton}
                aria-label='End Call'
                color='red'
                iconName='call_end'
                onClick={onHangup}
              />
            </Tooltip>
          )}
          {canAnswer && (
            <Tooltip direction='top' text='Answer'>
              <Button
                aria-label='Answer'
                color='primary'
                iconName='phone'
                onClick={onAnswer}
              />
            </Tooltip>
          )}
        </ButtonGroup>
      </Stack>
    </div>
  );
};

export default CallControls;
